import { useEffect, useRef, useState } from "react";
import api, { apiError, isAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MessageCircle, Users, Plus, Send, Hash, Loader2, ArrowLeft, UserPlus, Trash2 } from "lucide-react";
import { initials } from "@/lib/format";

const roleLabel = (r) => ({ super_admin: "Super Admin", manager: "Manager", admin: "Admin", hr: "HR", employee: "Employee" }[r] || r);
const relTime = (d) => {
  if (!d) return "";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(d).toLocaleDateString([], { month: "short", day: "numeric" });
};

export default function Chat() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [header, setHeader] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [contacts, setContacts] = useState([]);
  const [teamDlg, setTeamDlg] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();

  const loadChannels = async () => { try { setChannels((await api.get("/chat/channels")).data); } catch {} };
  const loadContacts = async () => { try { setContacts((await api.get("/chat/contacts")).data); } catch {} };
  const loadMessages = async (chan) => {
    if (!chan) return;
    try { const { data } = await api.get(`/chat/messages?channel_id=${encodeURIComponent(chan)}`); setMessages(data.messages); setHeader(data.header); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadChannels(); loadContacts(); }, []);
  useEffect(() => { if (active) loadMessages(active); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => {
    const t = setInterval(() => { loadChannels(); loadContacts(); api.post("/chat/presence").catch(() => {}); if (active) loadMessages(active); }, 5000);
    api.post("/chat/presence").catch(() => {});
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setSending(true);
    try { await api.post("/chat/messages", { channel_id: active, text: t }); setText(""); await loadMessages(active); loadChannels(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const startDm = (c) => {
    setDmOpen(false);
    const chan = "dm:" + [user.id, c.id].sort().join(":");
    setActive(chan); setHeader({ type: "dm", name: c.name, role: c.role }); setMessages([]);
  };

  const openTeam = (t) => setTeamDlg(t ? { id: t.team_id, name: t.name, description: "", color: t.color || "emerald", member_ids: [] } : { name: "", description: "", color: "emerald", member_ids: [] });

  const saveTeam = async () => {
    const d = teamDlg;
    if (!d.name.trim()) { toast.error("Team name is required"); return; }
    try {
      if (d.id) await api.patch(`/chat/teams/${d.id}`, { name: d.name, description: d.description, color: d.color, member_ids: d.member_ids });
      else await api.post("/chat/teams", { name: d.name, description: d.description, color: d.color, member_ids: d.member_ids });
      toast.success("Team saved"); setTeamDlg(null); loadChannels();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteTeam = async () => {
    if (!teamDlg?.id) return;
    try { await api.delete(`/chat/teams/${teamDlg.id}`); toast.success("Team deleted"); setTeamDlg(null); setActive(null); loadChannels(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleMember = (id) => setTeamDlg((d) => ({ ...d, member_ids: d.member_ids.includes(id) ? d.member_ids.filter((x) => x !== id) : [...d.member_ids, id] }));

  const Sidebar = (
    <div className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-72 shrink-0 border-r border-emerald-500/10 min-h-0`} data-testid="chat-sidebar">
      <div className="p-3 flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 border-emerald-500/30" onClick={() => setDmOpen(true)} data-testid="new-dm-btn"><UserPlus className="w-4 h-4 mr-1" /> New chat</Button>
        {admin && <Button size="sm" className="flex-1 bg-emerald-500 text-[#062217]" onClick={() => openTeam(null)} data-testid="new-team-btn"><Plus className="w-4 h-4 mr-1" /> Team</Button>}
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
        {channels.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No conversations yet. Start a chat or create a team.</p>}
        {channels.map((c) => (
          <button key={c.channel_id} onClick={() => setActive(c.channel_id)} data-testid={`channel-${c.channel_id}`}
            className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors ${active === c.channel_id ? "bg-emerald-500/15" : "hover:bg-white/5"}`}>
            <div className="relative shrink-0">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold ${c.type === "team" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-foreground"}`}>
                {c.type === "team" ? <Hash className="w-4 h-4" /> : initials(c.name)}
              </div>
              {c.type === "dm" && c.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0A0D0C]" data-testid="online-dot" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{c.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{relTime(c.last_at)}</span>
              </div>
              <p className="text-xs text-muted-foreground truncate">{c.last_message || (c.type === "team" ? `${c.members} members` : "Say hello")}</p>
            </div>
            {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-emerald-500 text-[#062217] text-[10px] font-bold grid place-items-center shrink-0">{c.unread}</span>}
          </button>
        ))}
      </div>
      <div className="border-t border-emerald-500/10 p-2 max-h-48 overflow-y-auto" data-testid="online-now-list">
        <p className="px-1 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Online now</p>
        {contacts.filter((c) => c.online).length === 0
          ? <p className="px-1 py-1 text-xs text-muted-foreground">No one else is online.</p>
          : contacts.filter((c) => c.online).slice(0, 25).map((c) => (
            <button key={c.id} onClick={() => startDm(c)} className="w-full flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-white/5 text-left" data-testid={`online-${c.id}`}>
              <span className="relative shrink-0">
                <span className="w-7 h-7 rounded-full bg-white/10 grid place-items-center text-[11px] font-semibold">{initials(c.name)}</span>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0A0D0C]" />
              </span>
              <span className="text-sm truncate">{c.name}</span>
            </button>
          ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Chat</h1></div>
      <div className="card-emerald p-0 overflow-hidden flex h-[70vh] min-h-[460px]" data-testid="chat-container">
        {Sidebar}
        <div className={`${active ? "flex" : "hidden md:flex"} flex-col flex-1 min-w-0`}>
          {!active ? (
            <div className="flex-1 grid place-items-center text-center text-muted-foreground p-6">
              <div><MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">Select a conversation to start messaging.</p></div>
            </div>
          ) : (
            <>
              <div className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-emerald-500/10">
                <button className="md:hidden" onClick={() => setActive(null)} data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
                <div className={`w-8 h-8 rounded-lg grid place-items-center text-xs font-semibold ${header?.type === "team" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10"}`}>{header?.type === "team" ? <Hash className="w-4 h-4" /> : initials(header?.name)}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" data-testid="chat-header-name">{header?.name}</p>
                  <p className="text-[11px] text-muted-foreground">{header?.type === "team" ? `${header?.members || 0} members` : (header?.online ? "Online now" : header?.last_seen ? `Last seen ${relTime(header.last_seen)}` : roleLabel(header?.role))}</p>
                </div>
                {header?.type === "team" && admin && active && (
                  <Button size="icon" variant="ghost" className="ml-auto h-8 w-8" onClick={() => setTeamDlg({ id: active.split(":")[1], name: header.name, description: "", color: "emerald", member_ids: [] })} data-testid="edit-team-btn"><Users className="w-4 h-4" /></Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-emerald-500 text-[#062217]" : "bg-white/8 text-foreground"}`}>
                        {!mine && header?.type === "team" && <p className="text-[11px] font-semibold text-emerald-300 mb-0.5">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "text-[#062217]/70 justify-end" : "text-muted-foreground"}`}>{relTime(m.created_at)}{mine && header?.type === "dm" && (header?.other_last_read && header.other_last_read >= m.created_at ? <span title="Seen" data-testid="seen-tick">✓✓</span> : <span title="Delivered">✓</span>)}</p>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No messages yet. Send the first one.</p>}
                <div ref={bottomRef} />
              </div>
              <div className="shrink-0 p-3 border-t border-emerald-500/10 flex items-end gap-2">
                <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1} placeholder="Type a message…" className="bg-[#0A0D0C] border-emerald-500/20 resize-none min-h-[42px] max-h-32" data-testid="chat-input" />
                <Button onClick={send} disabled={sending || !text.trim()} className="bg-emerald-500 text-[#062217] shrink-0" data-testid="chat-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New DM dialog */}
      <Dialog open={dmOpen} onOpenChange={setDmOpen}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20 max-h-[80vh] overflow-y-auto" data-testid="new-dm-dialog">
          <DialogHeader><DialogTitle>Start a conversation</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {contacts.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No colleagues to message yet.</p>}
            {contacts.map((c) => (
              <button key={c.id} onClick={() => startDm(c)} data-testid={`contact-${c.id}`} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left">
                <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-300 grid place-items-center text-sm font-semibold">{initials(c.name)}</div>
                <div className="min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground">{roleLabel(c.role)}</p></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Team create/edit dialog */}
      <Dialog open={!!teamDlg} onOpenChange={(o) => !o && setTeamDlg(null)}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20 max-h-[85vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader><DialogTitle>{teamDlg?.id ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
          {teamDlg && (
            <div className="space-y-3">
              <div><label className="text-xs text-muted-foreground">Team name</label><Input value={teamDlg.name} onChange={(e) => setTeamDlg({ ...teamDlg, name: e.target.value })} className="bg-[#0A0D0C] border-emerald-500/20 mt-1" data-testid="team-name-input" /></div>
              <div><label className="text-xs text-muted-foreground">Description</label><Input value={teamDlg.description} onChange={(e) => setTeamDlg({ ...teamDlg, description: e.target.value })} className="bg-[#0A0D0C] border-emerald-500/20 mt-1" /></div>
              <div>
                <label className="text-xs text-muted-foreground">Members ({teamDlg.member_ids.length})</label>
                <div className="mt-1 max-h-52 overflow-y-auto border border-emerald-500/15 rounded-lg p-1 space-y-0.5">
                  {contacts.map((c) => (
                    <button key={c.id} type="button" onClick={() => toggleMember(c.id)} data-testid={`team-member-${c.id}`} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm ${teamDlg.member_ids.includes(c.id) ? "bg-emerald-500/15" : "hover:bg-white/5"}`}>
                      <span className={`w-4 h-4 rounded border grid place-items-center ${teamDlg.member_ids.includes(c.id) ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>{teamDlg.member_ids.includes(c.id) && <span className="text-[#062217] text-[10px]">✓</span>}</span>
                      {c.name} <Badge className="ml-auto bg-white/10 text-[10px]">{roleLabel(c.role)}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {teamDlg?.id && <Button variant="outline" className="border-red-500/40 text-red-300 mr-auto" onClick={deleteTeam} data-testid="delete-team-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
            <Button className="bg-emerald-500 text-[#062217]" onClick={saveTeam} data-testid="save-team-btn">Save team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
